import React, { type FC } from 'react';
import { formatFloat } from '../../../utility/utility_functions';
export type QuickNetSheetTableProps = {
  fixedCosts: number;
  estimatedValue: number;
  maxBid: number;
  sqFt: number;
  estimatedProfit: number;
  estimatedValuePerSqft: number;
  maxBidPerSqft: number;
  roi: number;
  // Paid at closing out of sale proceeds: shown for reference, not part of cash all-in
  closingCosts?: number | null;
  // Holding cost for the approach's hold months: part of cash all-in
  proratedTaxes?: number | null;
};

const tdStyling = 'py-2 px-5 text-center text-xs';
const thStyling = 'py-3 px-3 text-semibold bg-indigo-600 text-xs text-white whitespace-nowrap text-center';

const QuickNetSheetTable: FC<QuickNetSheetTableProps> = ({ fixedCosts, estimatedValue, maxBid, sqFt, estimatedProfit, estimatedValuePerSqft, maxBidPerSqft, roi, closingCosts = null, proratedTaxes = null }) => {

  const perSqFt = (amount: number | null | undefined) => {
    if (amount != null && sqFt) {
      return amount / sqFt;
    }
  };

  const getFixedCostsPerSqFt = () => {
    if (fixedCosts != null && sqFt != null) {
      return (fixedCosts / sqFt);
    }
  };

  const getTotalGrossPerSqFt = () => {
    let totalGross = estimatedProfit;
    if (totalGross != null && sqFt != null) {
      return (totalGross / sqFt);
    }
  };

  const getCashAllIn = () => {
    if (maxBid != null && sqFt != null && fixedCosts != null) {
      return (maxBid + fixedCosts + (proratedTaxes ?? 0));
    }
  };

  const getCashAllInPerSqFt = () => {
    if (getCashAllIn() != null && sqFt != null) {
      return (getCashAllIn() / sqFt);
    }
  };

  return (
    <div className='h-full justify-center mx-auto rounded-lg border w-fit'>
      <table className='w-fit text-center rounded-lg'>
        <thead className='divide-y divide-gray-300'>
          <tr>
            <th className={`${thStyling} rounded-tl-lg`}>
              Expected Sale Amount
            </th>
            <td id='quick-net-sheet-expected-sale-amount' className={tdStyling}>
              {estimatedValue != null ? `\$${formatFloat(estimatedValue, 2)}` : '-'}
            </td>
            <td id='quick-net-sheet-expected-sale-amount-per-sqft' className={tdStyling}>
              {estimatedValuePerSqft != null ? `\$${formatFloat(estimatedValuePerSqft, 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={thStyling}>
              Purchase Price (Investment)
            </th>
            <td id='quick-net-sheet-purchase-price' className={tdStyling}>
              {maxBid != null ? `\$${formatFloat(maxBid, 2)}` : '-'}
            </td>
            <td id='quick-net-sheet-purchase-price-per-sqft' className={tdStyling}>
              {maxBidPerSqft != null ? `\$${formatFloat(maxBidPerSqft, 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={thStyling}>
              Fix Costs Estimate
            </th>
            <td id='quick-net-sheet-fix-costs-estimate' className={tdStyling}>
              {fixedCosts != null ? `\$${formatFloat(fixedCosts, 0)}` : '-'}
            </td>
            <td id='quick-net-sheet-fix-costs-estimate-per-sqft' className={tdStyling}>
              {getFixedCostsPerSqFt() != null ? `\$${formatFloat(getFixedCostsPerSqFt(), 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={thStyling}>
              Closing Costs
            </th>
            <td id='quick-net-sheet-closing-costs' className={tdStyling}>
              {closingCosts != null ? `\$${formatFloat(closingCosts, 0)}` : '-'}
            </td>
            <td id='quick-net-sheet-closing-costs-per-sqft' className={tdStyling}>
              {perSqFt(closingCosts) != null ? `\$${formatFloat(perSqFt(closingCosts), 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={thStyling}>
              Estimated Prorated Taxes
            </th>
            <td id='quick-net-sheet-estimated-prorated-taxes' className={tdStyling}>
              {proratedTaxes ? `\$${formatFloat(proratedTaxes, 0)}` : '-'}
            </td>
            <td id='quick-net-sheet-estimated-prorated-taxes-per-sqft' className={tdStyling}>
              {proratedTaxes && perSqFt(proratedTaxes) != null ? `\$${formatFloat(perSqFt(proratedTaxes), 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={thStyling}>
              Cash All-In
            </th>
            <td id='quick-net-sheet-cash-all-in' className={tdStyling}>
              {getCashAllIn() != null ? `\$${formatFloat(getCashAllIn(), 0)}` : '-'}
            </td>
            <td id='quick-net-sheet-cash-all-in-per-sqft' className={tdStyling}>
              {getCashAllInPerSqFt() != null ? `\$${formatFloat(getCashAllInPerSqFt(), 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={thStyling}>
              Total Gross
            </th>
            <td id='quick-net-sheet-total-gross' className={tdStyling}>
              {estimatedProfit != null ? `\$${formatFloat(estimatedProfit, 0)}` : '-'}
            </td>
            <td id='quick-net-sheet-total-gross-per-sqft' className={tdStyling}>
              {getTotalGrossPerSqFt() != null ? `\$${formatFloat(getTotalGrossPerSqFt(), 2)} / Sqft` : '-'}
            </td>
          </tr>
          <tr>
            <th className={`${thStyling} rounded-bl-lg`}>
              Gross ROI
            </th>
            <td id='quick-net-sheet-gross-roi' className={tdStyling}>
              {roi != null ? `${formatFloat(roi, 2)}\%` : '-'}
            </td>
            <td id='quick-net-sheet-gross-roi-per-sqft' className={tdStyling}>
              -
            </td>
          </tr>
        </thead>
      </table>
    </div>
  );
};

export default QuickNetSheetTable;
